/**
 * PriceCatalogue Component
 *
 * US-014: Price Catalogue Management
 * Displays and allows editing of explicit prices per product × grade × tier combination.
 * More intuitive than multiplier calculations - staff see actual prices.
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { toLocalDateString } from '../utils/dateUtils'
import {
  fetchProductPrices,
  updateProductPrice,
  updateProduct,
  recalculateCataloguePrices,
  fetchProducts,
  fetchProductTypes,
  fetchPriceTiers,
  fetchVolumeDiscounts,
  fetchPromotions
} from '../supabase-client'
import { useDataCache } from '../contexts/DataCacheContext'
import {
  filterCurrentAndUpcoming,
  filterByTierAndCustomerType,
  promoOptionLabel,
  promoToPdfRow,
} from '@/utils/promotionUtils'
import { buildVolumeDiscountRow } from '@/utils/priceListUtils'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog'
import {
  AlertCircle,
  Check,
  ChevronDown,
  Loader2,
  X,
  Filter,
  ShoppingCart,
  Store,
  Warehouse,
  UtensilsCrossed,
  Calculator,
  Download
} from 'lucide-react'

/**
 * Transform groupedPrices into the PriceGroup[] format expected by PriceListPdf.
 */
function toPriceGroups(groupedPrices, { selectedTiers, selectedProducts, selectedGrades }) {
  return groupedPrices
    .filter(product => selectedProducts.size === 0 || selectedProducts.has(product.productId))
    .map(product => ({
      product_name: product.productName,
      base_price: product.basePrice,
      limited_availability: product.limitedAvailability || false,
      grades: Object.values(product.grades)
        .filter(grade => selectedGrades.size === 0 || selectedGrades.has(grade.gradeId))
        .sort((a, b) => b.multiplier - a.multiplier)
        .map(grade => ({
          grade_name: grade.gradeName,
          multiplier: grade.multiplier,
          tiers: Object.fromEntries(
            Object.entries(grade.tiers)
              .filter(([tierName]) => selectedTiers.has(tierName))
              .map(([tierName, tierData]) => [tierName, tierData.price])
          ),
        }))
        .filter(grade => Object.values(grade.tiers).some(p => p > 0)),
    }))
    .filter(group => group.grades.length > 0)
}

export default function PriceCatalogue() {
  const { invalidateCache } = useDataCache()
  const [prices, setPrices] = useState([])
  const [products, setProducts] = useState([])
  const [productTypes, setProductTypes] = useState([])
  const [priceTiers, setPriceTiers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [saving, setSaving] = useState(false)
  const [recalculating, setRecalculating] = useState(false)

  // Filters
  const [selectedProducts, setSelectedProducts] = useState(() => new Set()) // empty = all
  const [selectedGrades, setSelectedGrades] = useState(() => new Set())
  const [filterTier, setFilterTier] = useState('all')
  const [includeVolumePricing, setIncludeVolumePricing] = useState(true)
  const [allPromotions, setAllPromotions] = useState([])
  const [selectedPromotions, setSelectedPromotions] = useState(() => new Set())

  // PDF generation
  const [generating, setGenerating] = useState(false)
  const [downloadModalOpen, setDownloadModalOpen] = useState(false)

  // Recalculate confirmation dialog
  const [recalculateConfirmOpen, setRecalculateConfirmOpen] = useState(false)

  // Editing state - track edited prices by ID
  const [editedPrices, setEditedPrices] = useState({})
  const [editingCell, setEditingCell] = useState(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [pricesData, productsData, typesData, tiersData, promotionsRaw] = await Promise.all([
        fetchProductPrices(),
        fetchProducts(),
        fetchProductTypes(),
        fetchPriceTiers(),
        fetchPromotions()
      ])

      setPrices(pricesData)
      setProducts(productsData.filter(p => p.active))
      setProductTypes(typesData.filter(t => t.active))
      setPriceTiers(tiersData)
      setAllPromotions(filterCurrentAndUpcoming(promotionsRaw || []))
    } catch (err) {
      console.error('Failed to load price catalogue:', err)
      setError('Failed to load price catalogue. Please refresh the page.')
    } finally {
      setLoading(false)
    }
  }

  // Group prices by product for display
  const groupedPrices = useMemo(() => {
    const grouped = {}

    prices.filter(p => p.product?.active && p.product_type?.active).forEach(price => {
      const productId = price.product_id
      const productName = price.product?.strain || 'Unknown'

      if (!grouped[productId]) {
        grouped[productId] = {
          productId,
          productName,
          basePrice: price.product?.base_price_per_kg || 0,
          limitedAvailability: price.product?.limited_availability || false,
          grades: {}
        }
      }

      const gradeId = price.product_type_id
      const gradeName = price.product_type?.name || 'Unknown'

      if (!grouped[productId].grades[gradeId]) {
        grouped[productId].grades[gradeId] = {
          gradeId,
          gradeName,
          multiplier: price.product_type?.price_multiplier || 1.0,
          tiers: {}
        }
      }

      const tierName = price.price_tier?.name || 'unknown'
      grouped[productId].grades[gradeId].tiers[tierName] = {
        id: price.id,
        price: parseFloat(price.price_per_kg),
        tierDisplayName: price.price_tier?.display_name || tierName
      }
    })

    // Convert to sorted array
    return Object.values(grouped)
      .sort((a, b) => a.productName.localeCompare(b.productName))
  }, [prices])

  const toggleProduct = (productId) => {
    setSelectedProducts(prev => {
      const next = new Set(prev)
      if (next.has(productId)) next.delete(productId)
      else next.add(productId)
      return next
    })
  }

  // Apply filters
  const filteredPrices = useMemo(() => {
    return groupedPrices.filter(product => {
      if (selectedProducts.size > 0 && !selectedProducts.has(product.productId)) return false
      return true
    }).map(product => {
      if (selectedGrades.size === 0) return product

      const filteredGrades = {}
      Object.entries(product.grades).forEach(([gradeId, grade]) => {
        if (selectedGrades.has(gradeId)) {
          filteredGrades[gradeId] = grade
        }
      })

      return { ...product, grades: filteredGrades }
    }).filter(product => Object.keys(product.grades).length > 0)
  }, [groupedPrices, selectedProducts, selectedGrades])

  const handleToggleLimitedAvailability = async (productId, checked) => {
    try {
      await updateProduct(productId, { limited_availability: checked })
      await loadData()
    } catch (err) {
      console.error('Failed to update limited availability:', err)
      setError('Failed to update limited availability.')
    }
  }

  const handlePriceChange = (priceId, value) => {
    setEditedPrices(prev => ({
      ...prev,
      [priceId]: value
    }))
  }

  const handleSavePrice = async (priceId) => {
    const newPrice = editedPrices[priceId]
    if (newPrice === undefined) return

    const numericPrice = parseFloat(newPrice)
    if (isNaN(numericPrice) || numericPrice < 0) {
      setError('Price must be a valid number >= 0')
      return
    }

    try {
      setSaving(true)
      setError(null)
      await updateProductPrice(priceId, { price_per_kg: numericPrice })

      // Update local state
      setPrices(prev => prev.map(p =>
        p.id === priceId ? { ...p, price_per_kg: numericPrice.toFixed(2) } : p
      ))

      // Clear edited state for this price
      setEditedPrices(prev => {
        const newState = { ...prev }
        delete newState[priceId]
        return newState
      })
      setEditingCell(null)

      invalidateCache('productPrices')
      setSuccess('Price updated')
      setTimeout(() => setSuccess(null), 2000)
    } catch (err) {
      console.error('Failed to update price:', err)
      setError('Failed to update price: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleCancelEdit = (priceId) => {
    setEditedPrices(prev => {
      const newState = { ...prev }
      delete newState[priceId]
      return newState
    })
    setEditingCell(null)
  }

  const handleRecalculate = async () => {
    setRecalculateConfirmOpen(false)
    try {
      setRecalculating(true)
      setError(null)
      const updated = await recalculateCataloguePrices()
      await loadData()
      invalidateCache('productPrices')
      invalidateCache('priceTiers')
      setSuccess(`Recalculated ${updated} prices`)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to recalculate prices:', err)
      setError('Failed to recalculate prices: ' + err.message)
    } finally {
      setRecalculating(false)
    }
  }

  // Sort tiers by sort_order for consistent column rendering
  const sortedTiers = useMemo(() =>
    [...priceTiers].sort((a, b) => a.sort_order - b.sort_order),
    [priceTiers]
  )

  // Tiers visible in the table (filtered)
  const visibleTiers = useMemo(() =>
    filterTier === 'all' ? sortedTiers : sortedTiers.filter(t => t.id === filterTier),
    [sortedTiers, filterTier]
  )

  const availablePromotions = useMemo(() => {
    const tierNames = new Set(visibleTiers.map(t => t.name))
    return filterByTierAndCustomerType(allPromotions, { tierNames })
  }, [allPromotions, visibleTiers])

  // Direct PDF download using current filters
  const handleDownloadPdf = useCallback(async () => {
    setGenerating(true)
    setError(null)
    try {
      const [{ pdf }, { PriceListPdf }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('./PriceListPdf'),
      ])

      const today = new Date().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })

      // Derive tier selection from filter
      const selectedTierNames = filterTier === 'all'
        ? new Set(priceTiers.map(t => t.name))
        : new Set(priceTiers.filter(t => t.id === filterTier).map(t => t.name))

      const pdfVisibleTiers = priceTiers
        .filter(t => selectedTierNames.has(t.name))
        .sort((a, b) => a.sort_order - b.sort_order)
      const pdfTiers = pdfVisibleTiers.map(t => ({ key: t.name, displayName: t.display_name }))
      const pdfGroups = toPriceGroups(groupedPrices, {
        selectedTiers: selectedTierNames,
        selectedProducts,
        selectedGrades,
      })

      const discountsRaw = includeVolumePricing ? await fetchVolumeDiscounts() : []
      const volumeDiscounts = discountsRaw
        .filter(d => d.active !== false)
        .map(d => buildVolumeDiscountRow(d, pdfGroups))

      const promotionsForPdf = availablePromotions
        .filter(p => selectedPromotions.has(p.id))
        .map(promoToPdfRow)

      // Generate the price list pages
      const priceListBlob = await pdf(
        PriceListPdf({
          grouped: pdfGroups,
          tiers: pdfTiers,
          generatedDate: today,
          volumeDiscounts,
          promotions: promotionsForPdf,
        })
      ).toBlob()

      // Merge cover page (landscape A4) with price list pages
      const { PDFDocument } = await import('pdf-lib')
      const [coverBytes, priceListBytes] = await Promise.all([
        fetch('/images/price-list-cover.pdf').then(r => r.arrayBuffer()),
        priceListBlob.arrayBuffer(),
      ])

      const merged = await PDFDocument.create()
      const coverDoc = await PDFDocument.load(coverBytes)
      const priceListDoc = await PDFDocument.load(priceListBytes)

      const [coverPage] = await merged.copyPages(coverDoc, [0])
      merged.addPage(coverPage)

      const priceListPages = await merged.copyPages(priceListDoc, priceListDoc.getPageIndices())
      priceListPages.forEach(page => merged.addPage(page))

      const mergedBytes = await merged.save()
      const finalBlob = new Blob([mergedBytes], { type: 'application/pdf' })

      const tierFileLabel = pdfVisibleTiers.length === 1
        ? pdfVisibleTiers[0].display_name
        : 'All-Tiers'
      const url = URL.createObjectURL(finalBlob)
      const a = document.createElement('a')
      a.href = url
      a.download = `GMC-Price-List-${tierFileLabel}-${toLocalDateString(new Date())}.pdf`
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      setDownloadModalOpen(false)
    } catch (err) {
      console.error('PDF generation failed:', err)
      setError('Failed to generate PDF: ' + err.message)
    } finally {
      setGenerating(false)
    }
  }, [filterTier, selectedGrades, selectedProducts, includeVolumePricing, availablePromotions, selectedPromotions, priceTiers, groupedPrices])

  const getTierIcon = (tierName) => {
    const iconProps = { className: 'h-4 w-4' }
    switch (tierName) {
      case 'retail':
        return <ShoppingCart {...iconProps} />
      case 'commercial':
        return <Store {...iconProps} />
      case 'restaurant':
        return <UtensilsCrossed {...iconProps} />
      case 'wholesale':
        return <Warehouse {...iconProps} />
      default:
        return null
    }
  }

  const getTierColor = (tierName) => {
    switch (tierName) {
      case 'retail':
        return 'text-blue-600'
      case 'commercial':
        return 'text-amber-600'
      case 'restaurant':
        return 'text-purple-600'
      case 'wholesale':
        return 'text-green-600'
      default:
        return 'text-gray-600'
    }
  }

  const hasUnsavedChanges = Object.keys(editedPrices).length > 0

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Price Catalogue</CardTitle>
          <CardDescription>Loading price data...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                Price Catalogue
              </CardTitle>
              <CardDescription>
                View and edit explicit prices per product, grade, and pricing tier.
                Click on any price to edit it directly.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setDownloadModalOpen(true)}
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
              <Button
                variant="outline"
                onClick={() => setRecalculateConfirmOpen(true)}
                disabled={recalculating || hasUnsavedChanges}
                className="gap-2"
              >
                <Calculator className="h-4 w-4" />
                {recalculating ? 'Recalculating...' : 'Recalculate from Multipliers'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert>
              <Check className="h-4 w-4" />
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {/* Filters */}
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border flex-wrap">
            <Filter className="h-4 w-4 text-slate-500" />
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Product:</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-48 justify-between font-normal">
                    {selectedProducts.size === 0
                      ? 'All Products'
                      : selectedProducts.size === 1
                        ? products.find(p => selectedProducts.has(p.id))?.strain || '1 selected'
                        : `${selectedProducts.size} selected`}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-2" align="start">
                  <div className="space-y-1">
                    <button
                      className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                      onClick={() => setSelectedProducts(new Set())}
                    >
                      <Checkbox checked={selectedProducts.size === 0} />
                      All Products
                    </button>
                    <div className="my-1 h-px bg-border" />
                    {products.map(p => (
                      <button
                        key={p.id}
                        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                        onClick={() => toggleProduct(p.id)}
                      >
                        <Checkbox checked={selectedProducts.has(p.id)} />
                        {p.strain}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Grade:</label>
              <MultiSelectDropdown
                options={productTypes.map(t => ({ id: t.id, label: t.name }))}
                selected={selectedGrades}
                onChange={setSelectedGrades}
                allLabel="All Grades"
                width="w-40"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Tier:</label>
              <Select value={filterTier} onValueChange={setFilterTier}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  {sortedTiers.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.display_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(selectedProducts.size > 0 || selectedGrades.size > 0 || filterTier !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedProducts(new Set())
                  setSelectedGrades(new Set())
                  setFilterTier('all')
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Tier Legend */}
          <div className="flex items-center gap-6 text-sm">
            <span className="text-muted-foreground">Pricing Tiers:</span>
            {priceTiers.map(tier => (
              <div key={tier.id} className={`flex items-center gap-1 ${getTierColor(tier.name)}`}>
                {getTierIcon(tier.name)}
                <span>{tier.display_name}</span>
                <Badge variant="outline" className="ml-1 text-xs">
                  {tier.multiplier}x
                </Badge>
              </div>
            ))}
          </div>

          {/* Price Table */}
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-48">Product</TableHead>
                  <TableHead className="w-32">Grade</TableHead>
                  {visibleTiers.map(tier => (
                    <TableHead key={tier.id} className="w-24 text-center">
                      <div className={`flex items-center justify-center gap-1 ${getTierColor(tier.name)}`}>
                        {getTierIcon(tier.name)}
                        {tier.display_name}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPrices.map(product => {
                  // Sort grades: paid items by multiplier descending, free items (Gift/Sample) at bottom
                  const sortedGrades = Object.entries(product.grades).sort(([, a], [, b]) => {
                    const aAllZero = Object.values(a.tiers).every(t => t.price === 0)
                    const bAllZero = Object.values(b.tiers).every(t => t.price === 0)

                    // Free items go to bottom
                    if (aAllZero && !bAllZero) return 1
                    if (!aAllZero && bAllZero) return -1

                    // Otherwise sort by multiplier descending (A+ Premium 1.6x first)
                    return b.multiplier - a.multiplier
                  })

                  return sortedGrades.map(([gradeId, grade], gradeIndex) => (
                    <TableRow key={`${product.productId}-${gradeId}`}>
                      {gradeIndex === 0 ? (
                        <TableCell
                          className="font-medium align-top"
                          rowSpan={Object.keys(product.grades).length}
                        >
                          <div>
                            {product.productName}
                            <div className="text-xs text-muted-foreground mt-1">
                              Base: £{product.basePrice?.toFixed(2)}/kg
                            </div>
                            <div className="flex items-center gap-1.5 mt-1">
                              <Checkbox
                                id={`la-${product.productId}`}
                                checked={product.limitedAvailability}
                                onCheckedChange={(checked) => handleToggleLimitedAvailability(product.productId, checked)}
                              />
                              <label htmlFor={`la-${product.productId}`} className="text-xs text-muted-foreground cursor-pointer">
                                Limited availability
                              </label>
                            </div>
                          </div>
                        </TableCell>
                      ) : null}
                      <TableCell>
                        <div>
                          {grade.gradeName}
                          <div className="text-xs text-muted-foreground">
                            {grade.multiplier}x
                          </div>
                        </div>
                      </TableCell>
                      {visibleTiers.map(tier => {
                        const tierData = grade.tiers[tier.name]
                        if (!tierData) return <TableCell key={tier.id}>-</TableCell>

                        const isEditing = editingCell === tierData.id
                        const editedValue = editedPrices[tierData.id]
                        const hasEdit = editedValue !== undefined

                        return (
                          <TableCell key={tier.id} className="text-center p-1">
                            {isEditing ? (
                              <div className="flex items-center gap-1">
                                <span className="text-muted-foreground">£</span>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={editedValue ?? tierData.price}
                                  onChange={(e) => handlePriceChange(tierData.id, e.target.value)}
                                  className="w-20 h-8 text-center"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSavePrice(tierData.id)
                                    if (e.key === 'Escape') handleCancelEdit(tierData.id)
                                  }}
                                />
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8"
                                  onClick={() => handleSavePrice(tierData.id)}
                                  disabled={saving}
                                >
                                  <Check className="h-4 w-4 text-green-600" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8"
                                  onClick={() => handleCancelEdit(tierData.id)}
                                >
                                  <X className="h-4 w-4 text-red-600" />
                                </Button>
                              </div>
                            ) : (
                              <button
                                className={`px-3 py-1 rounded hover:bg-slate-100 transition-colors ${
                                  hasEdit ? 'bg-amber-50 text-amber-700' : ''
                                }`}
                                onClick={() => setEditingCell(tierData.id)}
                              >
                                £{tierData.price.toFixed(2)}
                              </button>
                            )}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  ))
                })}
                {filteredPrices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2 + visibleTiers.length} className="text-center text-muted-foreground py-8">
                      No prices found. Check filters or run migration to populate catalogue.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Summary */}
          <div className="text-sm text-muted-foreground">
            Showing {filteredPrices.reduce((acc, p) => acc + Object.keys(p.grades).length, 0)} price rows
            for {filteredPrices.length} products
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={recalculateConfirmOpen} onOpenChange={setRecalculateConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recalculate from Multipliers</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            This will recalculate all prices from base price × grade multiplier × tier multiplier.
            Any manual price adjustments will be lost. This action can't be undone.
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRecalculateConfirmOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleRecalculate}>
              Recalculate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={downloadModalOpen} onOpenChange={(o) => !generating && setDownloadModalOpen(o)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Download Price List PDF</DialogTitle>
            <DialogDescription>
              Choose what extra information to include. Product, grade and tier are taken from the page filters.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={includeVolumePricing}
                onChange={(e) => setIncludeVolumePricing(e.target.checked)}
                className="rounded border-slate-300"
              />
              Include volume pricing
            </label>
            <div>
              <label className="text-sm font-medium mb-2 block">Promotions</label>
              <MultiSelectDropdown
                options={availablePromotions.map(p => ({ id: p.id, label: promoOptionLabel(p) }))}
                selected={selectedPromotions}
                onChange={setSelectedPromotions}
                allLabel="None"
                width="w-full"
                emptyLabel="No active or upcoming promotions for the selected tier"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDownloadModalOpen(false)} disabled={generating}>
              Cancel
            </Button>
            <Button onClick={handleDownloadPdf} disabled={generating} className="gap-2">
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Generate & Download
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
